import { useState, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangeFilter, useRange, initialRange } from "@/components/shared/DateRangeFilter";
import { inRange } from "@/lib/dateRange";
import { toDate } from "@/lib/format";
import { custLabel, custPhone, usePrivateContact } from "@/lib/customerIdentity";
import { MilestoneStatusSelect } from "@/components/projects/MilestoneStatusSelect";
import { MILESTONE_STATUSES, MILESTONE_PAYMENT_STATUSES } from "@/lib/fieldEnums";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StatCard } from "@/components/shared/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import * as Icons from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { text, list, inr, fmtTs, fmtDate, pct } from "@/lib/format";
import { AllFieldsPanel } from "@/components/explorer/AllFieldsPanel";
import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { BulkBar, SelectAllBox, SelectBox, useSelection } from "@/components/shared/BulkSelect";
import { NewDot, HealthChip, NewActivityBar, useRowSignals } from "@/components/shared/RowSignals";
import { useNotifications } from "@/context/NotificationsContext";
import { healthOf, worstOf, healthRowClass } from "@/lib/health";
import { cn } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";
import { DeliveryWorkspace } from "@/pages/SuperCrm";

const VIEWS = [["projects", "Projects"], ["plans", "Milestone plans"], ["milestones", "Milestones"], ["board", "By project"], ["delays", "Customer delays"]];

const PROJ_DATE_FIELDS = [{ key: "created_at", label: "Created" }, { key: "hired_at", label: "Hired" }, { key: "start_date", label: "Start date" }, { key: "updated_at", label: "Last updated" }];
// A project row is as hot as its worst milestone.
const projectHealth = (p, ms) => worstOf([healthOf("projects", p), ...ms.map((m) => healthOf("project_milestones", m))]);

const svcText = (p) => list(p.services, ", ", text(p.category || p.title));
const proIds = (p) => (Array.isArray(p.professional_ids) && p.professional_ids.length ? p.professional_ids : [p.professional_id].filter(Boolean));
const DONE = ["approved", "completed", "paid", "verified", "done"];

export default function Projects() {
  const projects = useCollection("projects");
  const users = useCollection("users");
  const pros = useCollection("professionals");
  const milestones = useCollection("project_milestones");
  const requests = useCollection("requests");
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some(([k]) => k === params.get("view")) ? params.get("view") : "projects";
  const setView = (v) => setParams((pr) => { pr.set("view", v); return pr; }, { replace: true });
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [dr, setDr] = useState(initialRange());
  const range = useRange(dr);

  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const reqById = useMemo(() => Object.fromEntries(requests.map((r) => [r.id, r])), [requests]);
  const proById = useMemo(() => Object.fromEntries(pros.map((p) => [p.id, p])), [pros]);
  const msByProject = useMemo(() => { const m = {}; milestones.forEach((x) => { (m[x.project_id] ||= []).push(x); }); Object.values(m).forEach((a) => a.sort((x, y) => (x.order || 0) - (y.order || 0))); return m; }, [milestones]);
  const statuses = useMemo(() => Array.from(new Set(projects.map((p) => p.status).filter(Boolean))).sort(), [projects]);
  const reqOf = (p) => reqById[p.request_id || p.source_request_id];

  const rows = projects
    .filter((p) => tab === "all" ? true : tab === "promise" ? p.brugo_promise : tab === "active" ? p.status === "active" : true)
    .filter((p) => statusF === "all" || p.status === statusF)
    .filter((p) => inRange(p[dr.field], range))
    .filter((p) => !q || (JSON.stringify(p) + (custLabel(userById[p.customer_id], reqOf(p)) || "")).toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (toDate(b.created_at)?.getTime() || 0) - (toDate(a.created_at)?.getTime() || 0));
  const sel = projects.find((p) => p.id === openId) || null;
  const selMs = sel ? (msByProject[sel.id] || []) : [];
  const selProgress = selMs.length ? pct(selMs.filter((m) => DONE.includes(m.status) || m.payment_status === "paid").length, selMs.length) : 0;
  const selCust = sel ? userById[sel.customer_id] : null;
  const selReq = sel ? reqOf(sel) : null;
  const selContact = usePrivateContact(selReq?.id);
  const selCustName = sel ? (text(selContact?.contact_name, null) || custLabel(selCust, selReq) || sel.customer_id) : "";
  const selCustPhone = sel ? (text(selContact?.contact_phone, null) || custPhone(selCust, selReq) || "—") : "";

  const promiseCount = projects.filter((p) => p.brugo_promise).length;
  const sig = useRowSignals("projects", "projects");
  const { isRowNew, seeItems } = useNotifications();
  const msNew = (p) => (msByProject[p.id] || []).some((m) => isRowNew("project_milestones", m.id, "projects"));
  const sel2 = useSelection(rows);
  const hotCount = projects.filter((p) => projectHealth(p, msByProject[p.id] || [])).length;
  const openProject = (p) => { sig.opened(p.id); seeItems((msByProject[p.id] || []).map((m) => `project_milestones/${m.id}`)); setOpenId(p.id); };

  return (
    <div>
      <PageHeader title="Projects & Milestones" subtitle="Post-hire delivery from the shared `projects` collection with live milestone progress, customer, professional and financials."
        actions={<NewActivityBar section="projects" />} />

      <div className="mb-5 inline-flex flex-wrap rounded-xl border border-border bg-secondary/50 p-1 gap-0.5" data-testid="proj-views">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`proj-view-${k}`}
            className={cn("px-3.5 py-1.5 text-sm rounded-lg transition-colors", view === k ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground hover:text-foreground")}>{l}</button>
        ))}
      </div>

      {view !== "projects" && <DeliveryWorkspace key={view} tabs={[view]} section="projects" />}

      {view === "projects" && <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testId="proj-kpi-total" index={0} label="Projects" value={String(projects.length)} icon="FolderKanban" accent="primary" />
        <StatCard testId="proj-kpi-active" index={1} label="Active" value={String(projects.filter((p) => p.status === "active").length)} icon="Activity" accent="cyan" critical={hotCount > 0} />
        <StatCard testId="proj-kpi-promise" index={2} label="Brugo Promise" value={String(promiseCount)} icon="ShieldCheck" accent="emerald" />
        <StatCard testId="proj-kpi-milestones" index={3} label="Milestones" value={String(milestones.length)} icon="Milestone" accent="amber" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm"><Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input data-testid="proj-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, customer…" className="pl-9" /></div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="proj-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all" className="text-xs">All statuses</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
        </Select>
        <DateRangeFilter value={dr} onChange={setDr} fields={PROJ_DATE_FIELDS} testId="proj-date-filter" />
        <span className="text-xs text-muted-foreground font-mono" data-testid="proj-shown-count">{rows.length} shown</span>
        {hotCount > 0 && <span className="text-xs text-red-600 font-medium flex items-center gap-1"><Icons.Siren className="h-3.5 w-3.5" />{hotCount} critical / stuck</span>}
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="proj-tab-all">All ({projects.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="proj-tab-active">Active</TabsTrigger>
          <TabsTrigger value="promise" data-testid="proj-tab-promise">Brugo Promise</TabsTrigger>
        </TabsList>
      </Tabs>

      <BulkBar s={sel2} path="projects" rows={projects} label="projects" fields={[{ key: "status", label: "Status" }, { key: "promise_status", label: "Promise status" }, { key: "quotation_status", label: "Quotation status" }]} />
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8"><SelectAllBox s={sel2} testId="proj-select-all" /></TableHead>
            <TableHead className="w-3 px-0" />
            <TableHead>Project</TableHead><TableHead>Customer</TableHead><TableHead>Professional</TableHead>
            <TableHead>Progress</TableHead><TableHead>Value</TableHead><TableHead>Promise</TableHead><TableHead>Status</TableHead><TableHead className="text-right">View</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => {
              const ms = msByProject[p.id] || [];
              const prog = ms.length ? pct(ms.filter((m) => DONE.includes(m.status) || m.payment_status === "paid").length, ms.length) : 0;
              const firstPro = proById[proIds(p)[0]];
              const h = projectHealth(p, ms);
              return (
                <TableRow key={p.id} className={cn("hover:bg-accent/40 cursor-pointer", healthRowClass(h), sel2.sel.has(p.id) && "bg-primary/5")} onClick={() => openProject(p)} data-testid={`project-row-${p.id}`}>
                  <TableCell><SelectBox s={sel2} id={p.id} /></TableCell>
                  <TableCell className="px-0"><NewDot show={sig.isNew(p.id) || msNew(p)} title={msNew(p) ? "New milestone activity" : "New activity"} /></TableCell>
                  <TableCell><p className="font-medium text-foreground max-w-[220px] truncate">{text(p.title || svcText(p))}</p><p className="text-[11px] font-mono text-muted-foreground">{p.project_number ? `#${p.project_number}` : p.id}</p>{h && <div className="mt-1"><HealthChip h={h} /></div>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground" data-testid={`proj-customer-${p.id}`}>{custLabel(userById[p.customer_id], reqOf(p)) || <span className="font-mono text-[11px]">{text(custPhone(userById[p.customer_id], reqOf(p)), null) || String(p.customer_id || "—").slice(0, 10)}</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{text(firstPro?.full_name) === "—" ? <span className="font-mono text-[11px]">{text(proIds(p)[0])}</span> : text(firstPro?.full_name)}{proIds(p).length > 1 ? ` +${proIds(p).length - 1}` : ""}</TableCell>
                  <TableCell className="w-32"><div className="flex items-center gap-2"><Progress value={prog} className="h-1.5" /><span className="text-[11px] font-mono text-muted-foreground">{prog}%</span></div></TableCell>
                  <TableCell className="font-mono text-foreground text-sm">{inr(p.quotation_total || p.total_estimated_cost)}</TableCell>
                  <TableCell>{p.brugo_promise ? <StatusBadge status="on_track" /> : <span className="text-xs text-muted-foreground">Normal</span>}</TableCell>
                  <TableCell><StatusBadge status={p.status || "active"} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}><EditRecordButton path="projects" id={p.id} /><Button size="sm" variant="ghost" onClick={() => openProject(p)} data-testid={`proj-view-${p.id}`}>View</Button></TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">No projects in this view.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      </>}

      <Sheet open={!!sel} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="project-drawer">
          {sel && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="font-heading">{text(sel.title || svcText(sel))}</SheetTitle>
                <SheetDescription className="font-mono">{sel.project_number ? `#${sel.project_number} · ` : ""}{sel.id}</SheetDescription>
              </SheetHeader>

              <div className="mt-3 flex flex-wrap gap-2">
                <StatusBadge status={sel.status || "active"} />
                {sel.promise_status && <StatusBadge status={sel.promise_status} />}
                <span className="text-xs rounded-full border border-border px-2 py-0.5 text-muted-foreground capitalize">{sel.brugo_promise ? "Brugo Promise" : (text(sel.project_mode, "normal"))}</span>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Milestone progress</span><span className="text-xs font-mono text-foreground">{selProgress}%</span></div>
                <Progress value={selProgress} className="h-2" />
              </div>

              <Section icon="User" title="Customer & Site">
                <KV k="Customer" v={<span data-testid="proj-drawer-customer">{selCustName}</span>} />
                <KV k="Phone" v={selCustPhone} />
                <KV k="City" v={text(sel.city)} />
                <KV k="Address" v={text(sel.site_location?.address)} />
              </Section>

              <Section icon="Briefcase" title={`Professionals (${proIds(sel).length})`}>
                {proIds(sel).map((id) => { const pro = proById[id]; return <KV key={id} k={text(pro?.category_name, "Pro")} v={`${text(pro?.full_name) === "—" ? id : text(pro?.full_name)} · ${text(pro?.phone)}`} />; })}
                {proIds(sel).length === 0 && <p className="text-xs text-muted-foreground">No professional assigned.</p>}
              </Section>

              <Section icon="IndianRupee" title="Financials">
                <KV k="Quotation" v={sel.quotation_total ? inr(sel.quotation_total) : "—"} />
                <KV k="Estimated cost" v={sel.total_estimated_cost ? inr(sel.total_estimated_cost) : "—"} />
                <KV k="Quotation status" v={text(sel.quotation_status)} />
                <KV k="Duration" v={text(sel.estimated_duration)} />
              </Section>

              <div className="mt-5">
                <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icons.Milestone className="h-4 w-4 text-primary" /> Milestones ({selMs.length})<button onClick={() => { setOpenId(null); setView("board"); }} className="ml-auto text-xs font-normal text-primary hover:underline">See all on the board →</button></h4>
                <div className="space-y-2">
                  {selMs.map((m) => {
                    const mh = healthOf("project_milestones", m);
                    return (
                      <div key={m.id} className={cn("rounded-lg border border-border p-3", healthRowClass(mh))} data-testid={`milestone-${m.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0"><p className="text-sm font-medium text-foreground flex items-center gap-1.5"><NewDot show={isRowNew("project_milestones", m.id, "projects")} />{m.order}. {text(m.title)}</p><p className="text-[11px] text-muted-foreground">Expected: {fmtDate(m.expected_start_date)} → {fmtDate(m.expected_end_date)}</p>{mh && <div className="mt-1"><HealthChip h={mh} /></div>}</div>
                          <div className="text-right shrink-0"><p className="font-mono text-sm text-foreground flex items-center justify-end gap-1">{inr(m.amount)}<EditRecordButton path="project_milestones" id={m.id} /></p><div className="flex gap-1 justify-end mt-1"><MilestoneStatusSelect m={m} options={MILESTONE_STATUSES} /><MilestoneStatusSelect m={m} field="payment_status" options={MILESTONE_PAYMENT_STATUSES} /></div></div>
                        </div>
                      </div>
                    );
                  })}
                  {selMs.length === 0 && <p className="text-xs text-muted-foreground py-2">No milestones for this project.</p>}
                </div>
              </div>

              {Array.isArray(sel.recent_changes) && sel.recent_changes.length > 0 && (
                <Section icon="History" title="Recent Changes">
                  {sel.recent_changes.map((c, i) => <div key={i} className="text-xs text-muted-foreground"><span className="text-foreground">{text(c.change)}</span> · {fmtTs(c.at)}</div>)}
                </Section>
              )}
              <AllFieldsPanel path="projects" id={sel.id} related={[
                { label: "Milestones", path: "project_milestones", filters: [["project_id", "==", sel.id]], describe: (d) => `${d.order ?? ""}. ${d.title || ""} · ${d.status} · ${d.payment_status}` },
                { label: "Milestone plans", path: "milestone_plans", filters: [["project_id", "==", sel.id]], describe: (d) => `${d.status || ""}` },
              ]} />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Section({ icon, title, children }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="mt-5">
      <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-primary" /> {title}</h4>
      <div className="rounded-lg border border-border p-3 space-y-1.5">{children}</div>
    </div>
  );
}
const KV = ({ k, v }) => <div className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground capitalize shrink-0">{k}</span><span className="text-foreground text-right break-words">{v}</span></div>;
